import { type EventExtraData, PAYMENT_METHOD, isDefined, isEmpty } from '@nfctron/nexus-core';

import { type UseInitCartPageData } from '@root/cart/cart/_resources/components/CartContext';
import { type EventCartPageProps } from '@root/cart/cart/index.page';

import { type PageWithCommonProps } from '@root/lib/modules/next/next.module';
import { type GtagJsAdapterRegisterConfig } from '@root/lib/modules/tracking/gtag-js.adapter';
import { type SklikConversionEvent } from '@root/lib/modules/tracking/sklik.adapter';

import { type CartStoreExtraProductsSlice, type CartStoreTicketTypesSlice } from '../../cart/_resources/stores/cartStore';

export const getOrgPartnerDataOverride = (
	orgPartner: CartStoreTicketTypesSlice.TicketTypeOrgPartnerConfig,
): CartStoreTicketTypesSlice.TicketTypeOrgPartnerConfig => {
	/** TODO: use orgPartnerId instead of namePub */
	switch (orgPartner.namePub) {
		/** NPP Magenta Moments case */
		case 'Magenta Moments': {
			orgPartner.orgPartnerBackgroundColor = '#E20074';
			orgPartner.orgPartnerForegroundColor = '#FFFFFF';
			break;
		}
		/** Výstaviště Brno case */
		case 'Výstaviště Brno': {
			orgPartner.orgPartnerBackgroundColor = '#FE0201';
			orgPartner.orgPartnerForegroundColor = '#FFFFFF';
			break;
		}
		default: {
			break;
		}
	}
	return orgPartner;
};

export const getTicketProductGroupOverride = (
	productGroup: CartStoreExtraProductsSlice.ExtraProductGroupConfig,
): CartStoreExtraProductsSlice.ExtraProductGroupConfig => {
	/** TODO: Hrady CZ 2025 */
	switch (productGroup.productGroupId) {
		case 'd7b875f4-98bf-4697-9fad-3014d606610c':
		case 'e7d1b6a4-244d-4864-9305-e8f6c0695c78':
		case '20b09635-3ba3-4e84-95b5-bbbd79015694':
		case '7699cded-53e4-494a-b156-b9b85aa81ae6':
		case '0f8823ae-5723-46a3-bec6-e621c9df9f48':
		case 'e70ec485-09da-4dac-bb24-05135963c31f':
		case '844ce2e0-88ec-4c6c-98ce-101af7be1992':
		case 'cb69a284-2e46-4672-96b9-1b53ca6ce7af': {
			/** limit only to one product from the group (free merch) */
			productGroup.quantityLimit = 1;
			return productGroup;
		}
		default: {
			return productGroup;
		}
	}
};

const HRADY_CZ_EVENTS = [
	/** Točník */
	'b2126307-c486-4113-b7d9-ec0b4c0dcaff',
	/** Veveří */
	'10ea8aa5-9674-49d7-b83d-4e8bc2cc7a9b',
	/** Švihov */
	'552d7658-e96f-47ee-b41c-8aeeee960999',
	/** Rožmberk */
	'd7b37cd0-dc78-4f32-82cd-559945711321',
	/** Kunětická Hora */
	'1bb5d121-9529-45cd-916c-eac235d7b362',
	/** Hradec nad Moravicí */
	'a8bf9e8c-96b6-42bf-a4be-1e1cb797358a',
	/** Bouzov */
	'437d0e8e-4f69-4142-85e9-0fec5ce0f422',
	/** Bezděz */
	'6a442a4c-e472-49bb-a8ac-87bd01ff9d5f',
];

export const getEventPageOverride = (props: EventCartPageProps): EventCartPageProps => {
	/** FIXME: Hrady CZ 2025 */
	if (HRADY_CZ_EVENTS.includes(props.eventData.eventId)) {
		props.eventData = { ...props.eventData, generateEmptyChips: true };
		props.eventData.defaultTicketTypeGroup = { ticketTypeGroupId: 'ostatni', namePub: 'Ostatní vstupenky', displayOrder: 10, extraData: {} };
	}

	/** FIXME: ClickToPay campaign demo with Vysočina Fest 2025 */
	if (props.eventData.slug.includes('vysocina-fest-2025')) {
		props.eventData.paymentMethods = props.eventData.paymentMethods.concat({ paymentMethodId: PAYMENT_METHOD.CLICK_TO_PAY, displayOrder: 3 });
		props.eventData.defaultTicketTypeGroup = { ticketTypeGroupId: '3', namePub: 'Ostatní vstupenky', displayOrder: 3, extraData: {} };
	}

	return props;
};

export const getTicketTypeDataOverride = (ticketType: CartStoreTicketTypesSlice.TicketTypeConfig): CartStoreTicketTypesSlice.TicketTypeConfig => {
	/** FIXME: HradyCZ case */
	if (HRADY_CZ_EVENTS.includes(ticketType.eventId)) {
		/** @see https://www.hradycz.cz/vstupenky/ */
		if (
			/** VIP kemp PLUS tickets */
			ticketType.namePub.toLowerCase().includes('vip kemp plus') &&
			/** but only base, not PARTA or FAMILY variants */
			!/family|parta/gi.test(ticketType.namePub)
		) {
			/** "lze koupit jen 2 najednou" = set min quantity and quantifier for adding to cart to 2 (max qty is defined from ticket type config already) */
			ticketType.extraData.addToCartQty = 2;
		}

		/** TODO: HradyCZ KOOPERATIVA benefit codes */
		if (
			/** kooperativa benefit tickets */
			ticketType.orgPartner?.benefitCode?.startsWith('KOOP_')
		) {
			if (ticketType.price === 0) {
				/** hide zero-amount tickets */
				ticketType.__isListable = false;
			} else {
				/** double the price for 1+1 benefit tickets */
				ticketType.extraData.originalPrice = ticketType.price * 2;
				// FIXME: append zero-amount tickets to the 1+1 benefit tickets
				switch (ticketType.ticketTypeId) {
					/** Hrady CZ - Bezděz 2025 */
					case 'e372adf8-6d52-4752-8abe-9343d75063c9': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'dcf0ca62-6e28-42aa-9adc-66c59e799e29', quantity: 1 }];
						break;
					}
					case 'efb4df14-0875-4c87-b417-71da3db6b033': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5c36e83f-88f5-44d9-8d40-842fc412c106', quantity: 1 }];
						break;
					}
					case '4de62cfa-9cbb-4f7a-ae5f-32d26bd79501': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '84b16ea1-61f7-4846-98b5-7990a64e14a4', quantity: 1 }];
						break;
					}
					case '8ab82705-8b1e-44a8-861c-4cd0a4e01e2f': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'ea27d058-cca4-48d2-9e07-04dd2a13fbe1', quantity: 1 }];
						break;
					}
					case '45800cfa-2071-4cf0-8011-989cb0fd759b': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'f8a64dfd-90b0-4b1f-bf88-a4b6b87d4f46', quantity: 1 }];
						break;
					}
					case '491b775a-cab2-4b0c-9213-808af2bc18eb': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '7e90a2ed-e16f-4908-814c-b61cf832cb13', quantity: 1 }];
						break;
					}
					case 'ed05c160-9b43-490e-8dce-b74de5746db9': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '60237371-e8ea-44d4-984f-9a5a90826efe', quantity: 1 }];
						break;
					}
					case 'f19a3ca8-5a0e-495a-a1f7-daf34494dc60': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'cc8972cf-5c4d-4379-b131-e85f13e49536', quantity: 1 }];
						break;
					}
					case '12c690a0-dd25-4971-b67a-ba5474aed0ea': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '944b01ca-116d-4a3e-8ac9-27d9e15aac55', quantity: 1 }];
						break;
					}
					case '3206d000-0eaf-439a-91a4-d9230e9ab010': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '8c125539-0246-413f-9a84-05e0616d2bc6', quantity: 1 }];
						break;
					}
					case 'f56bc5bc-159a-44be-873d-efbddde7128a': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '994621a7-6e0d-4997-adf0-3867c9a3a682', quantity: 1 }];
						break;
					}
					case '03a3871d-5700-4af5-ab35-178767f1a7e4': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'ac8464f9-42f0-4507-a1b2-e1b030b72322', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Bouzov 2025 */
					case 'f50ad3c5-39a6-40b4-bd83-b5a09675094a': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '85b3433b-c55a-4e1b-a061-c5112b9e43ff', quantity: 1 }];
						break;
					}
					case '209ca55e-0137-466b-b481-cd8d081a4fc2': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5d164f58-ba35-480e-aaa9-315c7ff0065d', quantity: 1 }];
						break;
					}
					case '84523fea-cc38-4404-a039-28e024227631': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '4e18e82a-1363-48f0-a5f3-5e1a621d8fbc', quantity: 1 }];
						break;
					}
					case '0500b19f-9a6d-421c-b36d-268a58ae0d23': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '93aedb35-7cb1-4efb-9131-1e30bc64a996', quantity: 1 }];
						break;
					}
					case '476a327d-8464-496f-acfb-1b5f318bd185': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '4e9880a8-5903-4c9f-ba98-09770a3868b0', quantity: 1 }];
						break;
					}
					case '90f68e16-8396-4605-b4d0-782016ab9c5d': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'bfe6c39a-9514-4618-a40a-d9cae44a2a0e', quantity: 1 }];
						break;
					}
					case '07af0148-d4ca-402e-9368-908ee1eadcbc': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '1f3198a5-4450-4ccd-a6cc-c5750ecdbd8a', quantity: 1 }];
						break;
					}
					case '6dad4622-ee33-43a8-a9c9-04d405b321d6': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'f9df9708-ff78-47ea-bc8e-89a3d7b98f95', quantity: 1 }];
						break;
					}
					case '5eebc4ca-eb61-4a69-af44-eedc006ca772': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'cb93e73f-263e-4138-9c80-fa204edfac03', quantity: 1 }];
						break;
					}
					case '2e6eb8a1-fb5b-47d4-8ea4-1f4f68f5149c': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '8a55220c-b7dc-4ae9-b96d-ed4607e14a8f', quantity: 1 }];
						break;
					}
					case '1089b4ba-20ba-492b-a4bd-4598a1f9d016': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '6c1114f6-4c9d-4ae0-b06a-63947d568d06', quantity: 1 }];
						break;
					}
					case 'f94a9e3b-bb6a-4ee2-ae85-6980135db98b': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'c90007a5-a8ed-4af2-9abc-0763f85c9f9c', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Hradec nad Moravicí 2025 */
					case '79e449b9-e0cd-4200-8a4d-422ef58496b3': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5b526aab-c1ea-4cf5-ae0e-9f64a8bc1b2a', quantity: 1 }];
						break;
					}
					case 'd3f104b4-ece1-401d-ab5e-9f179ab6b881': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '410795ad-7546-4731-a50e-596a0fd0eafc', quantity: 1 }];
						break;
					}
					case 'a153c46b-2eeb-4250-abfb-a5e3b017c21e': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '610fa74a-8847-4033-b6c0-45a79999bfe3', quantity: 1 }];
						break;
					}
					case '73c78c1d-5240-4a1a-96ab-3da11bebdd45': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'dfdc5476-175b-4728-ad67-3d0cddac9cda', quantity: 1 }];
						break;
					}
					case 'c129821b-b610-4446-8122-9251ea298c77': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'd1b8e645-03e0-46ae-8fc4-26ca44705951', quantity: 1 }];
						break;
					}
					case '06ae1db5-da83-438c-97db-14acb0b1619f': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '24a52cec-756b-4820-969f-430835af551b', quantity: 1 }];
						break;
					}
					case 'dbcb9dbf-47eb-4038-a429-4d2947f19515': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'f951f5eb-10c2-4a93-856f-c46dc41fd874', quantity: 1 }];
						break;
					}
					case '22083b0e-8f88-49c1-a7da-22b3d103e858': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '546cfb36-b774-49c7-bd06-c70e1fe6d587', quantity: 1 }];
						break;
					}
					case 'df5fcc3f-3f1e-4eae-9bc8-9a718a50a51f': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'd0ff78ce-daaa-4671-8f56-dbefc383b128', quantity: 1 }];
						break;
					}
					case '26fb39e3-a0d7-4e10-b4ba-e7a907592be6': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '628c11ff-7513-4f8c-88d4-152859d7df73', quantity: 1 }];
						break;
					}
					case '3a778713-df50-4420-a29f-3d0d93a7488b': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'fecc2038-bd3b-4b2b-822e-2d94ba3b055e', quantity: 1 }];
						break;
					}
					case '9a245d60-7ce2-496f-bd77-dbdf75b11120': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'd71f675d-52ad-403e-a993-0c78573660d3', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Kunětická hora 2025 */
					case 'be13799c-ea95-4aa7-8aa6-e6788f7c685b': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '12137e27-2f3b-4bdf-b079-bb5127dcbaf3', quantity: 1 }];
						break;
					}
					case '87c5bba0-c17c-4ee1-a1ef-90bde9a5a093': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'b22dd11b-1b81-4cd2-a7b4-ca4104b12faf', quantity: 1 }];
						break;
					}
					case '0ee80374-26f1-425a-8e99-fe08f7f7010a': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '6638adf2-2129-4529-9709-3d8a72aa52f2', quantity: 1 }];
						break;
					}
					case '8e2aac68-d8db-4071-95c0-95d85f1777e3': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '0f46d98a-6419-4199-a5a3-1eb859b4676a', quantity: 1 }];
						break;
					}
					case 'af4e3413-d3f9-4b43-b7ba-585fc61600de': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '11871670-1c9f-494c-a7d9-681c649a0bed', quantity: 1 }];
						break;
					}
					case '244336ff-1c55-4e47-b93e-cc4eb05b22ee': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '7887b925-16a5-49c3-94f4-9ed20235dc64', quantity: 1 }];
						break;
					}
					case '450acb9f-2238-4304-83eb-df4f5d01fcc7': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'fd33fe9a-4b30-4538-b8ab-bdce3c36657a', quantity: 1 }];
						break;
					}
					case '273ec1cd-8f38-4f11-b03f-d88baab78ec2': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'a298ea5e-9cc4-44cd-9df8-65a0266f9cd4', quantity: 1 }];
						break;
					}
					case '84ad03f9-0c7e-4efb-ace2-3e50cd2fd029': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '4506e64e-c259-47e1-9532-69d4d0fff874', quantity: 1 }];
						break;
					}
					case 'ee336d26-910b-4216-8633-703aa2920fbf': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5141810a-3b67-4f9a-b23c-826ab4b77a1c', quantity: 1 }];
						break;
					}
					case '0a26426e-927d-428a-9936-34d2d9e72c03': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'e4f51c1f-ee7f-4d2e-8b78-1e59958d63f3', quantity: 1 }];
						break;
					}
					case '7b5e89eb-8d77-465f-aec1-36ac8b7eeb78': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '9ecf107f-d428-41f9-9ef5-c85b13b7538d', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Rožmberk nad Vltavou 2025 */
					case 'b8e9ae71-f688-48d4-af10-0082dfb01ee5': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '7f9dcecc-e5f6-4cf7-bedc-ca0d23d62a3d', quantity: 1 }];
						break;
					}
					case 'a8faa02e-2a63-47a3-88e9-b7697b8ed5f6': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '135a93e7-9df6-4d3d-9aa4-b36096ee8f47', quantity: 1 }];
						break;
					}
					case 'e24f7f21-9a0f-4ae8-8ae6-f3a597c2ef7c': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5bd69700-eac7-45db-8276-f000866c0f97', quantity: 1 }];
						break;
					}
					case '1c691b1c-910e-4bab-a935-37c940ac0747': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'fbf5f737-c671-4fc8-934e-a142ea402a73', quantity: 1 }];
						break;
					}
					case '365fe3d7-4d3a-4b3c-a6b6-e96dfca6d446': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '65b86f5c-bc99-45bf-a88e-9aad08d2b344', quantity: 1 }];
						break;
					}
					case 'cbcc6aa4-ebb2-450d-9fa0-b75f916a43d6': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '9c5c756b-82d5-4b5e-b1ae-9e985173340b', quantity: 1 }];
						break;
					}
					case '48fbb2e4-67b3-4122-829b-bffce563b244': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '438131fc-1ddb-4f4c-9585-61b60abaab60', quantity: 1 }];
						break;
					}
					case '13eed7ba-9e5a-43d6-94fa-cc7f44ef1c9e': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '19f62ae0-5cf9-4b4c-a49d-90ddecae9b46', quantity: 1 }];
						break;
					}
					case 'a99c12d0-1c03-4e28-81c2-756b1abbda3e': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'c2eea55d-811d-4270-9700-063d07a0c5d0', quantity: 1 }];
						break;
					}
					case 'ba78ba8c-eb03-4bdd-8072-7e14d41bf013': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'f9b7874e-0f43-4738-ad49-f1792a72b4d5', quantity: 1 }];
						break;
					}
					case 'f7ef2070-d6ac-4917-b4cf-437bf67f3f7f': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5e05e519-c670-4a32-9389-0974fd2a4e82', quantity: 1 }];
						break;
					}
					case 'a5697689-38fb-428e-9514-850447204c91': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '7bde0359-ad24-4576-aee1-7622c4293580', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Švihov 2025 */
					case 'b70381f6-f804-4285-a648-a97ec658ece2': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '1ca17633-1a37-4982-8f1c-524f7dad2c04', quantity: 1 }];
						break;
					}
					case '19f23238-b900-45e8-9cd4-bf5caea2359e': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '3f1b691c-2465-48d5-8f3c-0bc4b077d962', quantity: 1 }];
						break;
					}
					case '5f8d2270-ef64-47cb-9263-e7dfe73cd4ca': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '6245fe28-bd80-45c8-a86c-9357ff66836e', quantity: 1 }];
						break;
					}
					case '70ff6b98-3df8-4bbf-80d3-31ab091fa961': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'ea0fa3f9-1af5-4dcb-9e5a-a86041dac56d', quantity: 1 }];
						break;
					}
					case '35542c6f-77bb-43c6-aff5-1e83883c62d6': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'a26f6b81-93e2-4e08-ad78-76c5cda25ebe', quantity: 1 }];
						break;
					}
					case '057363fd-3be6-455f-8dcf-8487232e3422': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '6c97468d-7321-437a-a016-da23f13939c7', quantity: 1 }];
						break;
					}
					case '96471ca4-319d-4b1a-9eeb-b76a9f6ecc25': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'c38c5445-5f5e-4b4f-aee3-4b228c14e5d3', quantity: 1 }];
						break;
					}
					case '589d3072-c1c4-41f0-9469-865e7bf0bd2a': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '888c2a1d-c467-4dd7-8592-9d0c2a04b6af', quantity: 1 }];
						break;
					}
					case 'c69ffea0-8288-4e6c-aba5-5c88c300ddf4': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '7ab80606-7f39-4554-a4c1-8f5c391f1c1e', quantity: 1 }];
						break;
					}
					case '4bd60741-574e-490c-a002-8df92399cdf1': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '9f97dcca-a66e-4993-b9c6-aa1905d7c018', quantity: 1 }];
						break;
					}
					case '00f61656-9663-443a-a8ff-b9afde97332d': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'fcb80c31-893c-449a-b161-741a433c01e5', quantity: 1 }];
						break;
					}
					case 'ec45fee8-241e-4e9e-afe9-db10f26fcee2': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '327bebc5-16ae-4216-ab82-e607f9e395b3', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Točník 2025 */
					case 'a7ee4972-45fd-48cc-94ec-4d59f7558165': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '41e3164f-7cbd-40d9-9c5e-6008521d7f82', quantity: 1 }];
						break;
					}
					case '9a92c434-5bc1-4ee5-af2c-16041d299523': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'b757ef3c-a1f7-4821-8722-e0965dc46f4d', quantity: 1 }];
						break;
					}
					case '50f7ccb3-04bd-4297-b579-dae052558fdd': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '7fde5a88-9229-4b59-96a6-4d6e89e492a6', quantity: 1 }];
						break;
					}
					case 'ba26aa45-9946-4f49-b848-95e69d005151': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'be166da4-81b7-4838-9488-765d2fada1d9', quantity: 1 }];
						break;
					}
					case 'cc7edf7e-0f20-4eb8-a3da-453059797910': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '436b58d3-47b5-4674-8ab3-7c64b74e0bf7', quantity: 1 }];
						break;
					}
					case '65ebd563-077b-44e9-b738-83cef4991728': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'fcc90db7-155d-428a-ba6b-4242a44d405b', quantity: 1 }];
						break;
					}
					case 'd9067691-4953-4ec5-a00f-9a264a15770a': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '4f0cb3fb-7008-48fc-b2d3-2f312f66920d', quantity: 1 }];
						break;
					}
					case '1e4d3dba-4e71-426f-844f-ac961f3a3033': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'c0a53641-0994-4bcc-99f1-e3550d5d62a7', quantity: 1 }];
						break;
					}
					case 'd2442461-129c-441a-bbe6-adca2b0586ce': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '84135f48-0098-4408-9977-41876e6b6cee', quantity: 1 }];
						break;
					}
					case '86a4f34a-b1b4-491b-839b-25217de07940': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '39115762-8c6e-4d77-8103-5ce3dd7569cb', quantity: 1 }];
						break;
					}
					case '5fed9a5a-2d4a-4f77-b218-06c00bbd757b': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '4927cce8-346e-4e53-aff0-a783c84e976a', quantity: 1 }];
						break;
					}
					/** Hrady CZ - Veveří 2025 */
					case 'fb4c704b-96e7-432f-8b1b-c721f290685c': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '3662667c-90c3-4549-80df-5de71ef78146', quantity: 1 }];
						break;
					}
					case '71e67656-d4d3-4e2e-bb71-c3c4fd5e923f': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'bf86a771-19ea-4cff-b645-9bd2bf917f88', quantity: 1 }];
						break;
					}
					case 'f32231c4-e0f8-456e-8f87-ff6e18242cd8': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '0460532f-98f6-4b9d-bbac-7b60a9d1dec1', quantity: 1 }];
						break;
					}
					case '019705fb-5222-4afd-ba8a-1ee3e82ce106': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'ab849ac0-8d52-4a1c-8d75-0894da902eb2', quantity: 1 }];
						break;
					}
					case '3c2e78d3-4817-482d-b372-d6efe12e01ff': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'e8531436-bab7-47c7-a9aa-a0f493eb4153', quantity: 1 }];
						break;
					}
					case 'f00f1656-89a3-4bdd-91ae-36b49387af06': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '738d5328-02c8-4112-b4fc-b9ad9204ef1b', quantity: 1 }];
						break;
					}
					case 'e62ad724-eaff-4a29-859c-de6efb27d388': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'e297f1fd-04cf-49fd-bd44-c24cf58fd6c0', quantity: 1 }];
						break;
					}
					case 'eeee2a0d-2de2-43aa-9547-21d2014dfe7d': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '382543d2-b3a5-46e7-bc26-05023acf39c1', quantity: 1 }];
						break;
					}
					case 'a226e685-90db-4ae5-8511-c0c88d531aa2': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '5062431f-8a08-411e-bbf4-4bcd7e00f8f7', quantity: 1 }];
						break;
					}
					case '8279b93d-25b4-48b9-b5d1-f1b38e506d58': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'a0a39350-6bb9-4d57-ae0e-17e1a15faf1c', quantity: 1 }];
						break;
					}
					case 'e44d81bc-c15d-44bd-90df-006ee0708d4c': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: '23ac16ba-b27b-40f4-b029-080e04d9cfdf', quantity: 1 }];
						break;
					}
					case '60f8244d-1008-4c20-be52-090568d3d544': {
						ticketType.extraData.autoAppendTicketTypes = [{ ticketTypeId: 'fdaefcbb-5c5a-4c90-98e3-c7d3629c1294', quantity: 1 }];
						break;
					}
				}
			}

			return ticketType;
		}

		/** TODO: HradyCZ O2 benefit codes */
		if (
			/** o2 benefit tickets */
			ticketType.orgPartner?.benefitCode?.startsWith('O2_')
		) {
			/** it's a 10% sale discount code */
			ticketType.extraData.originalPrice = ticketType.price / 0.9;

			return ticketType;
		}
	}

	/** Magenta Moments ticket type group */
	if (ticketType.ticketTypeGroup?.namePub.toLocaleLowerCase().includes('magenta moments')) {
		/** @ts-expect-error: FIXME: not in extraData schema yet (waiting for new nexus release) */
		ticketType.ticketTypeGroup.extraData.logoUrl = 'https://images.nfctron.com/organizers/0fcb4ce3-8e43-435d-993f-1c522975b2c0.png';
	}

	/** Click to Pay ticket type group */
	if (ticketType.ticketTypeGroup?.namePub.toLocaleLowerCase().includes('click to pay')) {
		/** @ts-expect-error: FIXME: not in extraData schema yet (waiting for new nexus release) */
		ticketType.ticketTypeGroup.extraData.logoUrl =
			// MC symbol: https://i.imgur.com/gTHZ44Q.png
			'https://i.imgur.com/8XqZS6n.png';
	}

	switch (ticketType.ticketTypeId) {
		/** additionalFeatures for Vysočina Fest 2025 */
		case '52e4bdff-6f1d-40a3-9a2f-c1fb41371c01': {
			ticketType.extraData.additionalFeatures = {
				fastTrack: {
					label: 'Fast Track',
					icon: 'arrow-big-up-dash',
					tooltip: 'Rychlý vstup na festivalu',
					sheet: {
						title: 'S touto vstupenkou získáte Fast Track při vstupu na festival',
						descriptionMd: 'Díky této vstupence získáte přednostní a rychlejší vstup na festival.',
					},
				},
			};
			return ticketType;
		}
		default: {
			return ticketType;
		}
	}
};

export const getExtraProductDataOverride = (
	extraProduct: CartStoreExtraProductsSlice.ExtraProductConfig,
): CartStoreExtraProductsSlice.ExtraProductConfig => {
	return extraProduct;
};

/**
 * Creates event tracking configuration
 * @param eventData - Event data
 * @param extraData - Extra data
 * @returns Tracking configuration
 */
export function getEventTrackingOverride(eventData: Partial<UseInitCartPageData>, extraData?: EventExtraData) {
	const tracking = {
		/** google tag manager containers */
		googleTagManager:
			extraData?.additionalTrackingIds
				?.filter(({ provider, id }) => provider === 'gtm' && id.startsWith('GTM'))
				.map(({ id }) => ({ trackingId: id })) ?? [],
		/** google tag tracking ids */
		gtagJs:
			extraData?.additionalTrackingIds
				?.filter(({ provider, id }) => provider === 'gtm' && !id.startsWith('GTM'))
				.map(({ id }) => ({ trackingId: id })) ?? [],
		/** facebook/meta pixel tracking ids */
		fbPixel: extraData?.additionalTrackingIds?.filter(({ provider }) => provider === 'fbp').map(({ id }) => ({ trackingId: id })) ?? [],
		/** tiktok pixel tracking ids */
		ttqPixel: extraData?.additionalTrackingIds?.filter(({ provider }) => provider === 'ttq').map(({ id }) => ({ pixelId: id })) ?? [],
		/** sklik tracking ids */
		sklik:
			extraData?.additionalTrackingIds
				?.filter(({ provider }) => provider === 'sklik')
				.map(({ id }) => {
					const [eventType, conversionId] = id.split('/');
					if (isEmpty(eventType) || isEmpty(conversionId)) return undefined;
					/** retargeting id */
					if (eventType === 'retargeting') return { retargetingId: conversionId! };
					/** conversion id */
					return { conversionId: conversionId!, eventType: eventType as SklikConversionEvent };
				})
				.filter(isDefined) ?? [],
	} satisfies PageWithCommonProps<unknown>['tracking'];

	/** add base tracking configuration */
	/** TODO: confusing naming gtmId in event.event but whatever */
	if (eventData.gtmId && eventData.gtmId.startsWith('GTM')) tracking.googleTagManager.push({ trackingId: eventData.gtmId });
	if (eventData.gtmId && !eventData.gtmId.startsWith('GTM')) tracking.gtagJs.push({ trackingId: eventData.gtmId });
	if (eventData.fbPixelId) tracking.fbPixel.push({ trackingId: eventData.fbPixelId });

	/** FIXME: temporary DINO LIVE advanced configuration hack, due to the lack of implementation */
	if (eventData.eventId === 'cb91ba9a-c783-4464-b2fb-13e6c249d052') {
		tracking.gtagJs = tracking.gtagJs.map((config): GtagJsAdapterRegisterConfig => {
			if (config.trackingId !== 'AW-16467360104') return config;
			/** override as per https://docs.google.com/spreadsheets/d/1XV_Y9f7GssQa5yTwWIPBaKiC8_LDJYrCPD-p24_9GrA/edit?gid=1368411895#gid=1368411895 */
			return { trackingId: 'AW-16467360104', conversionLabels: { addToCart: 'UAbVCJ6o8JYZEOjyn6w9', purchase: 'LNalCMu68JYZEOjyn6w9' } };
		});
	}

	/** FIXME: temporary Notino advanced configuration hack, due to the lack of implementation */
	if (eventData.eventId === '9e14a6fe-e8cf-4ce5-8ccc-bb3670cb174e') {
		tracking.gtagJs = tracking.gtagJs.map((config): GtagJsAdapterRegisterConfig => {
			if (config.trackingId !== 'AW-16595897436') return config;
			/** override as per https://publicisanalytics.notion.site/Notino-20-NFCtron-m-c-k-dy-944c342c36b6489b91adcfcf5087328d */
			return { trackingId: 'AW-16595897436', conversionLabels: { purchase: 'DubPCK3ttr8ZENyYxek9' } };
		});
	}

	return tracking;
}
